import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCreditCard, faMoneyBillWave } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import {jwtDecode} from "jwt-decode";
import axios from "axios";
import { createRazorpayOrder } from "./OnlinePaymentApi"; // Import the API function


const Payment = ({ selectedAddress, totalAfterDiscount, items }) => {
  const token = localStorage.getItem("STITCHD_ID");
  const navigate = useNavigate();
  const [showCODConfirmation, setShowCODConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const CODCharge = 100;

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to the top when the component is rendered
  }, []);

  const handlePayment = async (paymentType, orderResponse = null) => {
    setIsLoading(true);
    const decoded = jwtDecode(token);
    const userId = decoded._id;
    if (selectedAddress) {
      const combinedAddress = `${selectedAddress.address} | ${selectedAddress.landmark} | ${selectedAddress.city} - ${selectedAddress.postalCode}`;

      const orderItems = items?.map((item) => ({
        productId: item.productId._id,
        size: item.size,
        quantity: item.quantity,
      }));

      const orderValue =
        paymentType === "COD"
          ? totalAfterDiscount + CODCharge
          : totalAfterDiscount;

      const orderData = {
        name: selectedAddress.name,
        address: combinedAddress,
        phone: selectedAddress.phone,
        user: userId,
        item: orderItems,
        paymentType: paymentType,
        orderValue: orderValue,
        razorpayOrderId: orderResponse
          ? orderResponse.razorpay_order_id
          : "COD",
      };

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_URL}/order/add`,
          orderData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.success) {
          navigate("/orderPlaced");
        }
      } catch (error) {
        console.error("Error adding order:", error);
        alert("There was an error processing your order. Please try again.");
      } finally {
        setIsLoading(false);
      }
    } else {
      alert("Please select the delivery address");
      setIsLoading(false);
    }
  };

  const handlePaymentOnline = async () => {
    setIsLoading(true);
    if (selectedAddress) {
      const combinedAddress = `${selectedAddress.address} | ${selectedAddress.landmark} | ${selectedAddress.city} - ${selectedAddress.postalCode}`;
      try {
        const orderResponse = await createRazorpayOrder(totalAfterDiscount);
        const options = {
          key: "rzp_test_fo2Jp6Q6l8oBTa", // Replace with your Razorpay API key
          amount: orderResponse.amount,
          currency: "INR",
          name: "STITCHD",
          description: "Purchased On STITCHD",
          order_id: orderResponse.id,
          handler: function (response) {
            // Handle the payment success
            handlePayment("ONLINE", response);
          },
          prefill: {
            name: selectedAddress.name,
            contact: selectedAddress.phone,
          },
          notes: {
            address: combinedAddress,
          },
          theme: {
            color: "#F44336",
          },
        };
        const rzp1 = new window.Razorpay(options);
        rzp1.open();
      } catch (error) {
        console.error("Error creating Razorpay order:", error);
        alert("There was an error processing your order. Please try again.");
      } finally {
        setIsLoading(false);
      }
    } else {
      alert("Please select the delivery address");
      setIsLoading(false);
    }
  };

  const handleCODConfirmation = () => {
    if (selectedAddress) {
      setShowCODConfirmation(true);
    } else {
      alert("Please select the delivery address");
    }
  };

  const confirmCODPayment = () => {
    handlePayment("COD");
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="bg-gray-300 h-12 w-full rounded-md"></div>
        <div className="bg-gray-300 h-12 w-full rounded-md"></div>
      </div>
    );
  }

  return (
    <div className="font-twentieth-century">
      <div className="fixed top-0 left-0 w-full bg-white shadow-lg p-4 z-10 md:hidden">
        <div className="flex justify-between h-auto w-auto">
          <p className="font-semibold text-xl">Payment Method</p>
          <p className="text-gray-400 text-sm">STEP 2/2</p>
        </div>
      </div>
      <div className="p-6 flex flex-col items-center mt-20 md:mt-2">
        <div className="w-full">
          <button
            className="bg-red-100 p-3 rounded-lg shadow-sm mb-6 w-full border border-red-400 hover:bg-red-200 transition-colors duration-300"
            onClick={handlePaymentOnline}
          >
            <FontAwesomeIcon
              icon={faCreditCard}
              className="text-red-500 mr-3 md:text-xl"
            />
            <span className="text-xl font-medium">Pay Online</span>
            <p className="text-gray-600 text-sm mt-1">
              Secure and instant payment with UPI or Credit/Debit cards.
            </p>
          </button>

          <button
            className="bg-red-100 p-3 rounded-lg shadow-sm mb-6 w-full border border-red-400 hover:bg-red-200 transition-colors duration-300"
            onClick={handleCODConfirmation}
          >
            <FontAwesomeIcon
              icon={faMoneyBillWave}
              className="text-red-500 mr-3 md:text-xl"
            />
            <span className="text-xl font-medium">Cash on Delivery</span>
            <p className="text-gray-600 text-sm mt-1">
              Extra ₹100 will be charged for COD.
            </p>
          </button>
        </div>

        {showCODConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
            <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 md:w-1/3">
              <h2 className="text-xl font-semibold mb-4">
                Confirm Cash on Delivery
              </h2>
              <p className="text-gray-700 mb-4">
                An logistic charge of ₹100 will be applied for Cash on Delivery.
                Do you want to proceed?
              </p>
              <div className="flex justify-end">
                <button
                  className="bg-gray-300 text-gray-800 py-2 px-4 rounded-md mr-4 hover:bg-gray-400 transition-colors duration-300"
                  onClick={() => setShowCODConfirmation(false)}
                >
                  Cancel
                </button>
                <button
                  className="bg-red-500 text-white py-2 px-4 rounded-md hover:bg-red-600 transition-colors duration-300"
                  onClick={confirmCODPayment}
                  disabled={isLoading} // Disable button when loading
                >
                  {isLoading ? "Processing..." : "Confirm"} {/* Show loading text */}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payment;
